import type { StatusDefinition } from "@kontextwerk/web-sdk/core/display/status"
import {
  mdiAlertCircleOutline,
  mdiCheckBold,
  mdiInboxArrowDownOutline,
  mdiProgressClock,
} from "@mdi/js"

export type LastCommunicationSummaryCell = {
  label: string
  tooltip: string
}

export type RequiredActionSummaryCell = {
  definition: StatusDefinition<string>
  tooltip: string
}

export type CommunicationSummaryInput = {
  contact_step_id?: string | null
  lifecycle_status?: string | null
  delivery_status?: string | null
  business_outcome?: string | null
  provider_status?: string | null
  created_at?: string | null
  queued_at?: string | null
  sent_at?: string | null
  accepted_at?: string | null
  delivered_at?: string | null
  read_at?: string | null
  completed_at?: string | null
  failed_at?: string | null
  suppressed_at?: string | null
  canceled_at?: string | null
}

const formatDateTime = (value?: string | null) => {
  if (!value) return null

  const parsed = new Date(value)

  if (Number.isNaN(parsed.getTime())) return null

  return `${parsed.toLocaleDateString("de-DE")} ${parsed.toLocaleTimeString("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
  })}`
}

const communicationTimestamp = (communication: CommunicationSummaryInput) => {
  const candidates = [
    communication.read_at,
    communication.delivered_at,
    communication.completed_at,
    communication.sent_at,
    communication.accepted_at,
    communication.failed_at,
    communication.suppressed_at,
    communication.canceled_at,
    communication.queued_at,
    communication.created_at,
  ]
  for (const candidate of candidates) {
    const parsed = candidate ? new Date(candidate).getTime() : NaN

    if (!Number.isNaN(parsed)) return parsed
  }

  return 0
}

const normalizeStatus = (value?: string | null) => String(value || "").toUpperCase()

const communicationResult = (communication: CommunicationSummaryInput) => {
  const lifecycle = normalizeStatus(communication.lifecycle_status)
  const delivery = normalizeStatus(communication.delivery_status)
  const businessOutcome = normalizeStatus(communication.business_outcome)

  if (lifecycle === "FAILED" || delivery === "FAILED") return "Fehlgeschlagen"

  if (lifecycle === "SUPPRESSED") return "Nicht gesendet"

  if (delivery === "READ") return "Gelesen"

  if (delivery === "DELIVERED" || delivery === "MANUAL_DELIVERED") return "Zugestellt"

  if (delivery === "ACCEPTED") return "an Anbieter übergeben"

  if (delivery === "SENT") return "Gesendet"

  if (delivery === "QUEUED" || lifecycle === "QUEUED" || lifecycle === "IN_PROGRESS")
    return "Wird gesendet"

  if (businessOutcome === "CONFIRMED") return "Bestätigt"

  if (businessOutcome === "RESCHEDULED") return "Verschoben"

  if (businessOutcome === "CANCELED") return "Abgesagt"

  return "Dokumentiert"
}

export const buildLastCommunicationSummaryFromCommunications = (
  communications: CommunicationSummaryInput[] = [],
): LastCommunicationSummaryCell => {
  if (!communications.length) {
    return {
      label: "Noch nicht ausgeführt",
      tooltip: "Dieser Kontaktschritt wurde noch nicht ausgeführt.",
    }
  }

  const latest =
    communications
      .slice()
      .sort((left, right) => communicationTimestamp(right) - communicationTimestamp(left))[0] ??
    null

  if (!latest) {
    return {
      label: "Noch nicht ausgeführt",
      tooltip: "Dieser Kontaktschritt wurde noch nicht ausgeführt.",
    }
  }

  const result = communicationResult(latest)
  const timestamp = formatDateTime(
    latest.read_at ??
      latest.delivered_at ??
      latest.completed_at ??
      latest.sent_at ??
      latest.accepted_at ??
      latest.failed_at ??
      latest.suppressed_at ??
      latest.canceled_at ??
      latest.queued_at ??
      latest.created_at,
  )
  const tooltip = [
    `${communications.length} dokumentierte Kommunikation${communications.length === 1 ? "" : "en"}.`,
    timestamp ? `Letzte Kommunikation: ${timestamp}.` : null,
    latest.provider_status ? `Providerstatus: ${latest.provider_status}.` : null,
  ]
    .filter(Boolean)
    .join(" ")

  return {
    label: result,
    tooltip,
  }
}

export const buildLastCommunicationSummary = (
  stepId: string | null | undefined,
  communications: CommunicationSummaryInput[] = [],
): LastCommunicationSummaryCell => {
  const stepCommunications = communications.filter(
    (communication) => String(communication.contact_step_id ?? "") === String(stepId ?? ""),
  )

  return buildLastCommunicationSummaryFromCommunications(stepCommunications)
}

const isOutstandingStatus = (status?: string | null) =>
  ["READY", "RETRY_SCHEDULED", "ERROR"].includes(normalizeStatus(status))
const canMarkLetterWaiting = (status?: string | null) =>
  !["WAITING", "DONE", "CANCELED", "BLOCKED"].includes(normalizeStatus(status))

export const buildRequiredActionSummary = ({
  channel,
  status,
  isFuture,
}: {
  channel?: string | null
  status?: string | null
  isFuture?: boolean
}): RequiredActionSummaryCell => {
  const normalizedChannel = String(channel || "").toUpperCase()
  const normalizedStatus = normalizeStatus(status)

  if (isFuture || ["WAITING", "RETRY_SCHEDULED"].includes(normalizedStatus)) {
    return {
      definition: {
        value: "waiting",
        label: "Wartet",
        color: "blue",
        icon: mdiProgressClock,
        description: "Dieser Kontaktschritt ist geplant oder wartet auf Rückmeldung.",
      },
      tooltip: "Für diesen Schritt ist aktuell keine manuelle Aktion nötig.",
    }
  }

  if (normalizedChannel === "LETTER" && canMarkLetterWaiting(normalizedStatus)) {
    return {
      definition: {
        value: "required",
        label: "Nötig",
        color: "amber",
        icon: mdiAlertCircleOutline,
        description: "Terminbrief drucken und zustellen.",
      },
      tooltip: "Brief drucken und zustellen. Danach als erledigt markieren.",
    }
  }

  if (isOutstandingStatus(normalizedStatus)) {
    return {
      definition: {
        value: "required",
        label: "Nötig",
        color: "orange",
        icon: mdiInboxArrowDownOutline,
        description: "Kommunikation starten.",
      },
      tooltip: "Der Kontaktschritt kann jetzt gestartet werden.",
    }
  }

  return {
    definition: {
      value: "none",
      label: "Nein",
      color: "emerald",
      icon: mdiCheckBold,
      description: "Keine Handlung erforderlich.",
    },
    tooltip: "Für diesen Schritt ist keine Handlung erforderlich.",
  }
}
